// 10 · Coffre — inventaire et prélèvement.
//
// Portage intégral de l'écran V2 « Inventaire coffre ». Trois choses le
// distinguent d'un formulaire ordinaire, et ce sont celles-là qu'il faut tenir :
//
//   • La sauvegarde est PAR SECTION (save_inventaire_section, RPC V2). Le
//     Honeywell écrit rouleaux / pièces / billets / enveloppes J+1 pendant
//     qu'on saisit les caisses fixes sur le PC : réécrire le blob entier
//     écraserait la capture en cours. Chaque section part seule, sous
//     SELECT … FOR UPDATE.
//   • Les totaux viennent de computeTotals (sotcp-core), la fonction partagée
//     PC / mobile — les chiffres affichés ici sont ceux de SotCP au centime.
//   • La validation n'est pas le client qui se juge lui-même. FlashCP n'a pas
//     de serveur Node : le recalcul que la V2 faisait dans une server action
//     est descendu dans la RPC fc_valider_inventaire (miroir SQL
//     fc_totaux_coffre). On lui envoie NOS chiffres pour comparaison, elle
//     journalise toute divergence, écrit SES valeurs, et refuse un écart
//     ≥ 0,01 € sans motif — jeton 'motif_ecart_requis'.
//
// L'inventaire du jour vit dans rapport.inventaireCoffre : la table
// inventaire_coffre est abandonnée depuis avril 2026.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BILLET_DENOMS, BILLET_KEY, DEFAULT_INV, ENTREE_TYPES, J1_PRESETS,
  PIECE_DENOMS, PIECE_KEY, PRESET_FIXES, ROULEAU_VALUES,
  sumCaisseFixes, sumEnveloppesJ1, sumEntrees,
  sumPieces, sumRouleaux,
} from 'sotcp-core/inventaireCoffreCalc'
import { safeNumber } from 'sotcp-core/safeNumber'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { useReglages } from '@/data/reglages'
import { VALEUR_ENVELOPPE, apportsSortis, etatApports } from '@/domain/derive'
import { consoliderBillets, lirePrelevSco, semisFixesAEcrire, totauxCoffre, totauxSco } from '@/domain/especes'
import { couleurEcart, fmtEcart, lampeEcart, motEcart, severite } from '@/domain/ecart'
import { nomAffiche, useAuth } from '@/lib/auth'
import { aller } from '@/lib/router'
import { fmtEur, heure, pluriel } from '@/lib/format'
import { jetonErreur, messageErreur } from '@/lib/errors'
import { Card, Chip, Kpis, Modale, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PropsEtape } from './types'

/** Même temporisation que la V2 : assez court pour ne rien perdre, assez long
 *  pour ne pas envoyer une RPC par frappe. */
const TEMPO_MS = 400

/** Le seuil du SERVEUR (fc_valider_inventaire), seul arbitre du motif obligatoire. */
const SEUIL_MOTIF = 0.01

/** L'entrée posée par l'étape 05b — elle se corrige là-bas, pas ici. */
const ID_ENTREE_SCO = 'sco_auto'

const COULEUR_ENTREE: Record<string, 'act' | 'ok' | 'warn' | 'neutre'> = {
  SCO: 'act', livraison: 'ok', FDC: 'warn', custom: 'neutre', trouve: 'warn',
}

/**
 * Les types de la V2, plus l'ARGENT TROUVÉ — deux champs par entrée, un
 * commentaire et un montant (handoff §6). Il est ajouté ici et non dans
 * `sotcp-core` : le paquet partagé décrit le modèle de la V2, qui ne connaît pas
 * ce type et dont l'écran d'inventaire n'a pas à changer.
 *
 * Son libellé est libre, comme celui d'une entrée personnalisée : c'est le
 * commentaire (« trouvé dans le tiroir de la caisse 3 »), et c'est lui que
 * l'étape 11 dicte à côté du montant.
 */
const TYPES_ENTREE: Array<{ type: string; label: string; nom: string }> = [
  ...ENTREE_TYPES.map((t) => ({ ...t, nom: t.label || 'Personnalisé' })),
  { type: 'trouve', label: '', nom: 'Argent trouvé' },
]

/** Les types dont le libellé est saisi : il porte l'information, pas le registre. */
const LIBELLE_LIBRE = new Set(['custom', 'trouve'])

type Section = api.SectionInventaire

interface Detail { denomination: number; quantite: number; total: number }
interface Enveloppe { id: string; amount: number; detail: Detail[]; numero?: string }
interface Fixe { id: string; name: string; default_amount: number; confirmed_amount: number }
interface Entree { id: string; type: string; label: string; amount: number }

/** `type` et non `interface` : computeTotals attend un Record<string, unknown>,
 *  auquel seul un alias de type est assignable. */
type Inv = {
  solde_hier: number
  rouleaux: Record<string, number>
  pieces: Record<string, number>
  billets: Record<string, number>
  caisse_fixes: Fixe[]
  prelevements_j: Enveloppe[]
  prelevements_j1: Enveloppe[]
  entrees_coffre: Entree[]
  commentaire_ecart: string
  status: string
  validated_at: string | null
  validated_by: string | null
  total_compte: number
  solde_attendu: number
  ecart: number
}

type Boite =
  | { sorte: 'fixe' }
  | { sorte: 'enveloppe'; cible: 'prelevements_j' | 'prelevements_j1' }
  | { sorte: 'entree' }
  | { sorte: 'valider' }
  | { sorte: 'rouvrir' }
  | null

interface Divergence { champ: string; client: number; serveur: number }

const uid = (): string =>
  globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const tableau = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const nb = (v: unknown): number => safeNumber(v, 0)
const txt = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Une quantité de rouleaux, de pièces ou de billets est un ENTIER positif :
 *  fc_totaux_coffre ne retient que les valeurs `^[0-9]+$`. Un « 2,5 » compté
 *  ici serait ignoré là-bas et fabriquerait un écart fantôme à la validation. */
const entierPositif = (v: unknown): number => Math.max(0, Math.floor(nb(v)))

const carte = (source: unknown, defauts: Record<string, number>): Record<string, number> => {
  const src = objet(source)
  const out: Record<string, number> = { ...defauts }
  for (const cle of Object.keys(defauts)) if (cle in src) out[cle] = entierPositif(src[cle])
  return out
}

const lireEnveloppes = (source: unknown): Enveloppe[] =>
  tableau(source).map((e) => {
    const o = objet(e)
    return {
      id: txt(o.id) || uid(),
      amount: nb(o.amount),
      detail: tableau(o.detail).map((d) => {
        const x = objet(d)
        return { denomination: nb(x.denomination), quantite: nb(x.quantite), total: nb(x.total) }
      }),
    }
  })

/** Les caisses fixes viennent des réglages SotCP ; à défaut, des presets 102 / 540
 *  du paquet partagé. Le montant confirmé démarre sur le montant par défaut. */
const lireFixes = (source: unknown, reglage: Array<{ id: string; name: string; default_amount: number }>): Fixe[] => {
  const brut = tableau(source)
  if (brut.length > 0) {
    return brut.map((f) => {
      const o = objet(f)
      return {
        id: txt(o.id) || uid(),
        name: txt(o.name),
        default_amount: nb(o.default_amount),
        confirmed_amount: nb(o.confirmed_amount),
      }
    })
  }
  const base = reglage.length > 0
    ? reglage.map((f) => ({
        id: String(f.id || `fix_${f.name}`), name: String(f.name ?? ''), default_amount: nb(f.default_amount),
      }))
    : PRESET_FIXES.map((f) => ({ id: f.id, name: f.name, default_amount: f.default_amount }))
  return base.map((f) => ({ ...f, confirmed_amount: f.default_amount }))
}

function hydrater(brut: Record<string, unknown>, reglage: Array<{ id: string; name: string; default_amount: number }>): Inv {
  return {
    solde_hier: nb(brut.solde_hier),
    rouleaux: carte(brut.rouleaux, DEFAULT_INV.rouleaux),
    pieces: carte(brut.pieces, DEFAULT_INV.pieces),
    billets: carte(brut.billets, DEFAULT_INV.billets),
    caisse_fixes: lireFixes(brut.caisse_fixes, reglage),
    prelevements_j: lireEnveloppes(brut.prelevements_j),
    prelevements_j1: lireEnveloppes(brut.prelevements_j1),
    entrees_coffre: tableau(brut.entrees_coffre).map((e) => {
      const o = objet(e)
      return { id: txt(o.id) || uid(), type: txt(o.type) || 'custom', label: txt(o.label), amount: nb(o.amount) }
    }),
    commentaire_ecart: txt(brut.commentaire_ecart),
    status: txt(brut.status) || 'open',
    validated_at: typeof brut.validated_at === 'string' ? brut.validated_at : null,
    validated_by: typeof brut.validated_by === 'string' ? brut.validated_by : null,
    total_compte: nb(brut.total_compte),
    solde_attendu: nb(brut.solde_attendu),
    ecart: nb(brut.ecart),
  }
}

const lireDivergences = (v: unknown): Divergence[] =>
  tableau(v).map((d) => {
    const o = objet(d)
    return { champ: txt(o.champ), client: nb(o.client), serveur: nb(o.serveur) }
  })

/** Entrée = avancer d'un champ dans le même tableau de dénominations. On compte
 *  vingt-trois cases debout : lever la main pour attraper la souris entre deux
 *  bacs de pièces coûte plus cher que tout le reste de l'écran.
 *  (Touche interceptée DANS un champ — pas de raccourci global, cf. blueprint.) */
function avancerDansTable(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (e.key !== 'Enter') return
  const table = e.currentTarget.closest('table')
  if (!table) return
  const champs = Array.from(table.querySelectorAll<HTMLInputElement>('input:not([disabled])'))
  const rang = champs.indexOf(e.currentTarget)
  // Sans cette garde, un champ introuvable dans la liste (rang −1) renverrait
  // sur champs[0] : Entrée sauterait EN HAUT du tableau, contenu sélectionné,
  // et la frappe suivante écraserait le premier comptage. Le pire des retours.
  if (rang < 0) return
  const suivant = champs[rang + 1]
  // Sur la dernière case on laisse passer la touche : dans une modale, Entrée
  // doit valider le formulaire, pas mourir dans le tableau.
  if (!suivant) return
  e.preventDefault()
  suivant.focus()
  suivant.select()
}

/* ── l'écran ─────────────────────────────────────────────────────────────── */

export default function Coffre({ rapport, lectureSeule }: PropsEtape) {
  const { date, journee, ecrireRapport, majRapport, recharger } = useRun()
  // Les causes proposées viennent des réglages : elles changent avec la
  // pratique, pas avec le code. Le texte libre reste ce qui fait foi.
  const { caisseFixes, quotaInventaires, causesEcartCoffre: CAUSES } = useReglages()
  const { user } = useAuth()
  const toast = useToast()

  const invRapport = useMemo(() => objet(rapport?.inventaireCoffre), [rapport])
  const [inv, setInv] = useState<Inv>(() => hydrater(invRapport, caisseFixes))
  const [boite, setBoite] = useState<Boite>(null)
  const [occupe, setOccupe] = useState(false)
  const [divergences, setDivergences] = useState<Divergence[]>([])
  /** L'entrée qui vient d'être ajoutée : son montant prend le focus tout seul. */
  const [entreeNeuve, setEntreeNeuve] = useState<string | null>(null)
  const [quota, setQuota] = useState<{ etat: 'repos' | 'compte' | 'fait'; valides: number; journees: number }>(
    { etat: 'repos', valides: 0, journees: 0 })

  // Une écriture différée s'exécute plusieurs rendus après la frappe : elle doit
  // lire l'état le plus frais, jamais la capture d'un rendu passé.
  const refInv = useRef(inv)
  const refRapport = useRef(invRapport)
  useEffect(() => { refInv.current = inv }, [inv])
  useEffect(() => { refRapport.current = invRapport }, [invRapport])
  // Le vidage SCO peut être saisi depuis un autre poste pendant qu'on compte :
  // la validation doit juger sur le plus frais, comme pour l'inventaire.
  const refSco = useRef<ReturnType<typeof lirePrelevSco>>(null)

  const enAttente = useRef<Partial<Record<Section, unknown>>>({})
  const minuteries = useRef<Partial<Record<Section, number>>>({})
  /** Vrai quand une mise à jour extérieure est arrivée pendant qu'on tapait :
   *  elle a été refusée pour ne pas effacer la frappe, elle reste due. */
  const resyncDu = useRef(false)

  const invValide = inv.status === 'validated'
  const journeeFermee = journee != null && journee.status !== 'active'
  const fige = lectureSeule || journeeFermee || invValide

  // save_inventaire_section ne connaît PAS le statut de l'inventaire : elle
  // refuse une journée clôturée, pas un inventaire validé. Le seul rempart
  // contre une écriture après validation est donc ici — d'où une garde unique
  // au point de passage obligé plutôt qu'un `disabled` par champ.
  const refFige = useRef(fige)
  refFige.current = fige

  const refRecharger = useRef(recharger)
  useEffect(() => { refRecharger.current = recharger }, [recharger])

  /**
   * Envoi d'une section. Le patch local porte tout l'inventaire tel que l'écran
   * l'affiche ; la RPC ne reçoit QUE la section — c'est elle qui empêche une
   * livraison Honeywell concurrente d'être écrasée. Si la RPC refuse,
   * ecrireRapport remet le rapport d'avant et l'effet de resynchronisation
   * ci-dessous ramène la saisie à la vérité du serveur.
   */
  const pousser = useCallback(async (section: Section) => {
    if (!(section in enAttente.current)) return
    const valeur = enAttente.current[section]
    delete enAttente.current[section]
    window.clearTimeout(minuteries.current[section])
    delete minuteries.current[section]
    try {
      await ecrireRapport(
        `inventaire de coffre · ${section}`,
        { inventaireCoffre: { ...refRapport.current, ...refInv.current, [section]: valeur } },
        () => api.enregistrerSectionInventaire(date, section, valeur),
        '10',
      )
    } catch { /* le provider a déjà posé le message et remis l'état d'avant */ }
    // Le patch optimiste réécrit TOUT l'inventaire local : une livraison
    // Honeywell arrivée pendant la frappe vient d'être recouverte ici, alors
    // qu'elle est bien en base (l'écriture est par section). Dès que la file
    // est vide, on redemande la vérité au serveur plutôt que de la perdre.
    if (resyncDu.current && Object.keys(enAttente.current).length === 0) {
      resyncDu.current = false
      try { await refRecharger.current() } catch { /* le provider a déjà parlé */ }
    }
  }, [date, ecrireRapport])

  const viderFile = useCallback(async () => {
    for (const s of Object.keys(enAttente.current) as Section[]) await pousser(s)
  }, [pousser])

  /**
   * Mutation d'UNE section, sur le modèle du mutateSection de la V2 : la
   * nouvelle valeur se calcule à partir de l'état courant (refInv), jamais de
   * la capture d'un rendu — deux frappes dans deux sections différentes avant
   * un même rendu ne peuvent pas s'annuler l'une l'autre.
   */
  const muter = useCallback(<S extends Section>(
    section: S, calcul: Inv[S] | ((precedent: Inv[S]) => Inv[S]),
  ) => {
    if (refFige.current) return
    const courant = refInv.current
    const valeur = typeof calcul === 'function'
      ? (calcul as (p: Inv[S]) => Inv[S])(courant[section])
      : calcul
    const suivant: Inv = { ...courant, [section]: valeur }
    refInv.current = suivant
    setInv(suivant)
    enAttente.current[section] = valeur
    window.clearTimeout(minuteries.current[section])
    minuteries.current[section] = window.setTimeout(() => { void pousser(section) }, TEMPO_MS)
  }, [pousser])

  // Temps réel : le Honeywell ou un autre poste a écrit dans la journée. On ne
  // se resynchronise QUE si aucune saisie n'est en vol — sinon on effacerait ce
  // que la personne est en train de taper. La mise à jour refusée n'est pas
  // perdue pour autant : elle est marquée due, et rejouée à file vide.
  useEffect(() => {
    if (Object.keys(enAttente.current).length > 0) { resyncDu.current = true; return }
    const frais = hydrater(invRapport, caisseFixes)
    // La référence est remise d'aplomb TOUT DE SUITE : une frappe qui arrive
    // avant le rendu suivant doit partir de ce que le serveur vient de dire,
    // pas de l'état d'avant la resynchronisation.
    refInv.current = frais
    setInv(frais)
  }, [invRapport, caisseFixes])

  // Les caisses fixes semées depuis les réglages sont AFFICHÉES et COMPTÉES
  // dès l'ouverture, mais rien ne les écrivait : le serveur ne somme que la
  // base. Écran à 0, serveur à −642, motif exigé pour un écart qu'on ne voit
  // pas. On écrit le semis une fois, à l'ouverture, et les deux comptes disent
  // la même chose. La garde `fige` de `muter` couvre journée close et
  // inventaire validé — on n'écrit jamais dans un dossier arrêté.
  const semisFait = useRef<string | null>(null)
  useEffect(() => {
    if (fige || semisFait.current === date) return
    if (!semisFixesAEcrire(invRapport.caisse_fixes)) return
    if (refInv.current.caisse_fixes.length === 0) return
    semisFait.current = date
    muter('caisse_fixes', refInv.current.caisse_fixes)
  }, [date, fige, invRapport, muter])

  // Quitter l'étape moins de 400 ms après une frappe ne doit pas la perdre.
  const refPousser = useRef(pousser)
  useEffect(() => { refPousser.current = pousser }, [pousser])
  useEffect(() => () => {
    for (const s of Object.keys(enAttente.current) as Section[]) void refPousser.current(s)
  }, [])

  /* ── totaux ───────────────────────────────────────────────────────────── */

  const apports = useMemo(() => {
    const src = objet(rapport?.apports)
    const { lignes } = etatApports(rapport)
    return {
      // Le total RETRANCHÉ du coffre porte les caissiers ET les monnayeurs :
      // les deux sortent la même monnaie du même meuble. Les lignes affichées,
      // elles, restent celles des caissiers — c'est l'étape 02A qui détaille
      // les machines.
      total: apportsSortis(rapport),
      lignes: lignes.map((l) => ({ ...l, ko: txt(objet(src[l.caissier]).saisie) === 'KO' })),
    }
  }, [rapport])

  // Le vidage des automates (05b) verse au coffre ses ENVELOPPES SCELLÉES : dans
  // le coffre sans être dans le comptage, puisqu'on ne les rouvre pas. Les
  // pièces et le vrac du même vidage, eux, sont mêlés au coffre et déjà pris
  // par le comptage physique — les ajouter les doublerait.
  const prelevSco = useMemo(() => lirePrelevSco(rapport), [rapport])
  const scoTotaux = useMemo(() => totauxSco(prelevSco), [prelevSco])
  useEffect(() => { refSco.current = prelevSco }, [prelevSco])

  // Le vidage a pu être saisi depuis un autre poste pendant que le coffre était
  // ouvert : on repose la ligne à l'arrivée. Le serveur ne réécrit que si elle a
  // bougé, et refuse si l'inventaire est déjà validé.
  useEffect(() => {
    if (lectureSeule || journeeFermee || invValide) return
    void api.synchroniserEntreeSco(date).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])
  const billetsConsolides = useMemo(
    () => consoliderBillets(inv.billets, prelevSco?.enveloppes ?? []),
    [inv.billets, prelevSco],
  )

  const totaux = useMemo(
    () => totauxCoffre({ inv, apportsTotal: apports.total, prelevSco }),
    [inv, apports.total, prelevSco],
  )

  // Une fois l'inventaire validé, les chiffres qui font foi sont ceux que le
  // SERVEUR a arrêtés et écrits en base — pas la recomputation locale. Quand le
  // serveur a corrigé quelque chose (c'est précisément ce que dit le bandeau
  // « divergences »), afficher les deux revient à mettre deux écarts différents
  // sur le même écran et à laisser deviner lequel part au bilan. Un seul.
  const ecran = invValide
    ? { total_compte: inv.total_compte, solde_attendu: inv.solde_attendu, ecart: inv.ecart }
    : totaux
  const tRouleaux = sumRouleaux(inv.rouleaux)
  const tPieces = sumPieces(inv.pieces)
  const tFixes = sumCaisseFixes(inv.caisse_fixes)
  const tJ = sumEnveloppesJ1(inv.prelevements_j)
  const tJ1 = sumEnveloppesJ1(inv.prelevements_j1)
  const tEntrees = sumEntrees(inv.entrees_coffre)

  // Le seuil du motif est celui du SERVEUR (0,01 €) : c'est lui qui refuse, il
  // ne sert à rien d'en tenir un deuxième à l'écran.
  const motif = inv.commentaire_ecart.trim()
  const motifObligatoire = Math.abs(ecran.ecart) >= SEUIL_MOTIF
  const motifManque = motifObligatoire && motif === ''
  // Un motif survivant à un écart redevenu nul ne doit pas rester invisible :
  // il serait resservi tel quel au prochain écart, sans que personne le relise.
  const motifOrphelin = !motifObligatoire && motif !== ''

  /* ── validation, réouverture ──────────────────────────────────────────── */

  const valider = async () => {
    setBoite(null)
    if (lectureSeule || journeeFermee || invValide || occupe) return
    setOccupe(true)
    try {
      // Le serveur recalcule à partir de la base : tout ce qui traîne dans la
      // file part AVANT, sinon il jugerait un inventaire périmé.
      await viderFile()
      // …et l'état a pu bouger pendant ce vidage (livraison Honeywell,
      // resynchronisation). On juge sur refInv, jamais sur la capture du rendu
      // qui a ouvert la modale.
      const courant = refInv.current
      const t = totauxCoffre({ inv: courant, apportsTotal: apports.total, prelevSco: refSco.current })
      const explication = courant.commentaire_ecart.trim()
      if (Math.abs(t.ecart) >= SEUIL_MOTIF && explication === '') {
        toast('MOTIF OBLIGATOIRE — UN ÉCART DE COFFRE NE SE VALIDE PAS SANS EXPLICATION', true)
        return
      }
      const res = await api.validerInventaire(
        date, t.total_compte, t.solde_attendu, t.ecart, explication)
      setDivergences(lireDivergences(res.divergences))
      // Ce sont SES chiffres qui sont écrits en base — on affiche les siens.
      majRapport({
        inventaireCoffre: {
          ...refRapport.current, ...refInv.current,
          status: 'validated', validated_at: new Date().toISOString(),
          // fc_acteur_nom() et nomAffiche() disent la même chose : afficher le
          // nom tout de suite évite un « validé par — » jusqu'au rechargement.
          validated_by: nomAffiche(user),
          commentaire_ecart: explication,
          total_compte: res.total_compte, solde_attendu: res.solde_attendu, ecart: res.ecart,
        },
      })
      toast(`INVENTAIRE VALIDÉ — ÉCART ${fmtEcart(res.ecart)}`)
      await recharger()
    } catch (e) {
      toast(jetonErreur(e) === 'motif_ecart_requis'
        ? 'MOTIF OBLIGATOIRE — UN ÉCART DE COFFRE NE SE VALIDE PAS SANS EXPLICATION'
        : messageErreur(e), true)
    } finally { setOccupe(false) }
  }

  const rouvrir = async () => {
    setBoite(null)
    if (lectureSeule || journeeFermee || occupe) return
    setOccupe(true)
    try {
      await api.rouvrirInventaire(date)
      // La RPC V2 ne remet à null QUE ces trois clés : le total compté, le
      // solde attendu et l'écart de la validation précédente restent en base
      // jusqu'à la prochaine. On reflète exactement ça.
      majRapport({
        inventaireCoffre: {
          ...refRapport.current, status: 'open', validated_at: null, validated_by: null,
        },
      })
      setDivergences([])
      toast('INVENTAIRE ROUVERT — LES SECTIONS REDEVIENNENT SAISISSABLES')
      await recharger()
    } catch (e) { toast(messageErreur(e), true) } finally { setOccupe(false) }
  }

  /* ── quota des inventaires du mois ────────────────────────────────────── */

  // À la demande : compter exige de relire le rapport complet de chaque journée
  // du mois. On ne paie ce prix que si quelqu'un pose la question.
  const compter = async () => {
    if (quota.etat === 'compte') return
    setQuota((q) => ({ ...q, etat: 'compte' }))
    try {
      const jours = await api.listerJournees(`${date.slice(0, 7)}-01`, date)
      const detail = await Promise.all(jours.map((j) => api.chargerJournee(j.date)))
      const valides = detail.filter(
        (j) => txt(objet(objet(j?.rapport).inventaireCoffre).status) === 'validated').length
      setQuota({ etat: 'fait', valides, journees: jours.length })
    } catch (e) {
      toast(messageErreur(e), true)
      setQuota((q) => ({ ...q, etat: 'repos' }))
    }
  }

  /* ── rendu ────────────────────────────────────────────────────────────── */

  return (
    <>
      <Kpis items={[
        { k: 'Total compté', v: fmtEur(ecran.total_compte), s: 'rouleaux + espèces + fixes + J+1' },
        { k: 'Solde attendu', v: fmtEur(ecran.solde_attendu), s: 'hier + entrées − J − apports' },
        {
          // Le nombre de l'écran. Sa couleur ne dépend jamais de son signe : un
          // excédent de 50 € est la même anomalie qu'un manque de 50 €.
          k: 'Écart coffre',
          v: fmtEcart(ecran.ecart),
          s: invValide ? `${motEcart(ecran.ecart)} · arrêté` : motEcart(ecran.ecart),
          couleur: couleurEcart(ecran.ecart),
        },
        {
          k: 'Prélèvements J',
          v: fmtEur(tJ),
          s: pluriel(inv.prelevements_j.length, 'enveloppe'),
        },
        { k: 'Apports du jour', v: fmtEur(apports.total), s: 'déduits du solde attendu' },
      ]} />

      {journeeFermee && (
        <Notice c="warn">
          <b>Journée SotCP clôturée</b> — l’inventaire ne s’écrit plus. Sa réouverture se fait
          dans SotCP.
        </Notice>
      )}

      {invValide && (
        // La lampe suit l'ÉCART arrêté, pas le fait d'avoir validé : un
        // inventaire validé avec 1 558 € d'écart n'est pas un bandeau vert.
        <Notice c={lampeEcart(inv.ecart)} action={
          <button className="btn warn sm" disabled={lectureSeule || journeeFermee || occupe}
                  data-encours={occupe ? 'true' : undefined}
                  onClick={() => setBoite({ sorte: 'rouvrir' })}>Rouvrir l’inventaire</button>
        }>
          <b>Inventaire validé</b> par {inv.validated_by ?? '—'} à {heure(inv.validated_at)} — chiffres
          arrêtés par le serveur : compté {fmtEur(inv.total_compte)} · attendu {fmtEur(inv.solde_attendu)} ·
          écart <b className="mo" style={{ color: couleurEcart(inv.ecart) }}>{fmtEcart(inv.ecart)}</b>.
        </Notice>
      )}

      {divergences.length > 0 && (
        <Notice c="warn">
          <b>Le serveur a recalculé et corrigé {pluriel(divergences.length, 'chiffre')}</b> — ce sont
          ses valeurs qui sont écrites, la divergence est au journal.
          <div className="hint" style={{ marginTop: 3 }}>
            {divergences.map((d) => (
              <div key={d.champ}>
                {d.champ.toUpperCase()} · ÉCRAN {fmtEur(d.client)} → SERVEUR {fmtEur(d.serveur)} ·{' '}
                <b style={{ color: couleurEcart(d.client - d.serveur) }}>
                  {fmtEcart(d.client - d.serveur)}
                </b>
              </div>
            ))}
          </div>
        </Notice>
      )}

      {/* 1 · solde de la veille */}
      <Card titre="Solde du coffre hier" actions={<span className="mo">{fmtEur(inv.solde_hier)}</span>}>
        <div className="slot">
          <span className="nm">Report du total compté de la veille</span>
          <span className="sp" />
          <Saisie valeur={inv.solde_hier} largeur={140} fige={fige} etiquette="Solde du coffre hier"
                  changer={(v) => muter('solde_hier', v)} />
        </div>
        <div className="hint" style={{ padding: '0 12px 9px' }}>
          SAISIE MANUELLE — C’EST LE SEUL CHIFFRE QUE PERSONNE NE PEUT DÉDUIRE À TA PLACE
        </div>
      </Card>

      {/* 2, 3, 4 · rouleaux, pièces, billets — vingt-trois cases dans l'ordre du
          bac : Tab et Entrée descendent, le contenu se sélectionne au focus. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: 12, marginTop: 12 }}>
        <Card titre="Rouleaux" actions={<span className="mo">{fmtEur(tRouleaux)}</span>}>
          <TableDenoms
            lignes={PIECE_DENOMS.map((d) => {
              const cle = PIECE_KEY(d)
              const unite = ROULEAU_VALUES[cle] ?? 0
              return {
                cle, libelle: `Rouleau ${cle.replace('.', ',')} €`, sous: fmtEur(unite),
                quantite: inv.rouleaux[cle] ?? 0, unite,
              }
            })}
            fige={fige}
            changer={(cle, q) => muter('rouleaux', (r) => ({ ...r, [cle]: q }))}
          />
          <div className="hint" style={{ padding: '8px 12px' }}>
            SAISI AU HONEYWELL EN TEMPS NORMAL · 10 c = 4,00 € ET 1 € = 25,00 €
          </div>
        </Card>

        <Card titre="Pièces" actions={<span className="mo">{fmtEur(tPieces)}</span>}>
          <TableDenoms
            lignes={PIECE_DENOMS.map((d) => {
              const cle = PIECE_KEY(d)
              return { cle, libelle: `${cle.replace('.', ',')} €`, quantite: inv.pieces[cle] ?? 0, unite: d }
            })}
            fige={fige}
            changer={(cle, q) => muter('pieces', (p) => ({ ...p, [cle]: q }))}
          />
        </Card>

        <Card titre="Billets"
              actions={<>
                {scoTotaux.enveloppes > 0 && <Chip c="act">DONT {fmtEur(scoTotaux.enveloppes)} SCELLÉS</Chip>}
                <span className="mo">{fmtEur(billetsConsolides.total)}</span>
              </>}>
          <TableDenoms
            lignes={BILLET_DENOMS.map((d) => {
              const cle = BILLET_KEY(d)
              const l = billetsConsolides.lignes.find((x) => x.denomination === d)
              return {
                cle, libelle: `${d} €`, quantite: inv.billets[cle] ?? 0, unite: d,
                venu: l?.enveloppes ?? 0,
              }
            })}
            fige={fige}
            colonneVenue="Enveloppes SCO"
            changer={(cle, q) => muter('billets', (b) => ({ ...b, [cle]: q }))}
          />
          {scoTotaux.enveloppes > 0 && (
            <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
              Les enveloppes du vidage SCO (étape 05b) sont scellées : elles ne se recomptent pas,
              mais elles sont bien dans le coffre. On ne saisit que le vrac — l’addition est faite
              ici, les deux termes en clair. Les pièces et les billets en vrac du même vidage ne
              traversent PAS : ils sont déjà dans ce que tu comptes.
            </div>
          )}
        </Card>
      </div>

      {/* 5 · caisses fixes */}
      <div style={{ marginTop: 12 }}>
        <Card titre="Caisses fixes" actions={<span className="mo">{fmtEur(tFixes)}</span>}>
          {inv.caisse_fixes.length === 0
            ? <Vide>Aucune caisse fixe configurée.</Vide>
            : inv.caisse_fixes.map((f) => {
              // L'écart entre le montant confirmé et le montant par défaut est
              // un écart comme un autre : il se colore comme les autres.
              const derive = f.confirmed_amount - f.default_amount
              return (
                <div className="slot" key={f.id}>
                  <span className="nm mo">{f.name}</span>
                  <span className="hint">défaut {fmtEur(f.default_amount)}</span>
                  {severite(derive) !== 'nul' && (
                    <span className="hint mo" title={motEcart(derive)}
                          style={{ color: couleurEcart(derive) }}>{fmtEcart(derive)}</span>
                  )}
                  <span className="sp" />
                  <Saisie valeur={f.confirmed_amount} largeur={120} fige={fige}
                          etiquette={`Montant confirmé de la caisse fixe ${f.name}`}
                          changer={(v) => muter('caisse_fixes',
                            (cf) => cf.map((x) => x.id === f.id ? { ...x, confirmed_amount: v } : x))} />
                  {/* Une caisse fixe des réglages ne se supprime pas ici : elle est
                      partagée avec SotCP. Seuls les ajouts du jour s'enlèvent. */}
                  {f.id.startsWith('fix_') ? <span style={{ width: 26 }} /> : (
                    <button className="btn stop sm" disabled={fige}
                            aria-label={`Retirer la caisse fixe ${f.name}`}
                            onClick={() => muter('caisse_fixes',
                              (cf) => cf.filter((x) => x.id !== f.id))}>✕</button>
                  )}
                </div>
              )
            })}
          {!fige && (
            <div style={{ padding: '9px 12px' }}>
              <button className="btn sm" onClick={() => setBoite({ sorte: 'fixe' })}>Ajouter une caisse fixe</button>
            </div>
          )}
        </Card>
      </div>

      {/* 6 · prélèvements du jour · 7 · enveloppes J+1 — le compte d'ENVELOPPES
          se lit à côté du montant : c'est en enveloppes qu'on les manipule. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 12, marginTop: 12 }}>
        <Card titre="Prélèvements J — envoyés aujourd’hui" actions={
          <span className="mo">{pluriel(inv.prelevements_j.length, 'env.', 'env.')} · {fmtEur(tJ)}</span>
        }>
          <ListeEnveloppes
            enveloppes={inv.prelevements_j} fige={fige}
            vide="Aucune enveloppe envoyée aujourd’hui."
            ajouter={(env) => muter('prelevements_j', (l) => [...l, env])}
            retirer={(id) => muter('prelevements_j', (l) => l.filter((e) => e.id !== id))}
            composer={() => setBoite({ sorte: 'enveloppe', cible: 'prelevements_j' })}
            numeroter={(id, numero) => muter('prelevements_j',
              (l) => l.map((e) => e.id === id ? { ...e, numero } : e))}
          />
          {/* Le blueprint les annonce « en lecture seule » parce qu'il les
              attend du scan Honeywell (mobile_add_prelevement_j). Ce module
              n'est pas dans ce build : les rendre non modifiables ici rendrait
              la section impossible à remplir, et le solde attendu faux. Elles
              restent saisissables, comme dans le client V2. */}
          <div className="hint" style={{ padding: '0 12px 9px' }}>
            DÉDUITS DU SOLDE ATTENDU — CE QUI EST PARTI N’EST PLUS DANS LE COFFRE ·
            SAISIE MANUELLE TANT QUE LE SCAN HONEYWELL N’EST PAS LÀ
          </div>
        </Card>

        <Card titre="Prélèvements J+1 — préparés pour demain" actions={
          <span className="mo">{pluriel(inv.prelevements_j1.length, 'env.', 'env.')} · {fmtEur(tJ1)}</span>
        }>
          <ListeEnveloppes
            enveloppes={inv.prelevements_j1} fige={fige}
            vide="Aucune enveloppe préparée pour demain."
            ajouter={(env) => muter('prelevements_j1', (l) => [...l, env])}
            retirer={(id) => muter('prelevements_j1', (l) => l.filter((e) => e.id !== id))}
            composer={() => setBoite({ sorte: 'enveloppe', cible: 'prelevements_j1' })}
          />
          <div className="hint" style={{ padding: '0 12px 9px' }}>
            COMPTÉS DANS LE TOTAL — ELLES SONT ENCORE DANS LE COFFRE CE SOIR
          </div>
        </Card>
      </div>

      {/* 8 · entrées de coffre */}
      <div style={{ marginTop: 12 }}>
        <Card titre="Entrées de coffre" actions={<span className="mo">{fmtEur(tEntrees)}</span>}>
          {inv.entrees_coffre.length === 0 && <Vide>Aucune entrée enregistrée.</Vide>}
          {inv.entrees_coffre.map((e) => {
            const auto = e.id === ID_ENTREE_SCO
            return (
            <div className="slot" key={e.id}>
              <Chip c={COULEUR_ENTREE[e.type] ?? 'neutre'}>{e.type}</Chip>
              {auto && <Chip c="act">DEPUIS 05B</Chip>}
              {/* Le libellé d'un type connu est celui du registre partagé : il ne
                  se réécrit pas, sinon deux journées ne se comparent plus. */}
              <input className="fld" style={{ flex: 1, padding: '5px 7px' }}
                     placeholder={e.type === 'trouve' ? 'Où, quoi — le commentaire' : 'Description'}
                     aria-label="Description de l’entrée"
                     value={e.label} disabled={fige || auto || !LIBELLE_LIBRE.has(e.type)}
                     onChange={(ev) => muter('entrees_coffre',
                       (l) => l.map((x) => x.id === e.id ? { ...x, label: ev.target.value } : x))} />
              {/* La ligne du vidage ne se retouche PAS ici : son montant est le
                  total compté à l'étape 05b. Le corriger de ce côté ferait dire
                  deux choses différentes au même argent. */}
              <Saisie valeur={e.amount} largeur={120} fige={fige || auto}
                      etiquette={`Montant de l’entrée ${e.label || e.type}`}
                      autoFocus={e.id === entreeNeuve}
                      changer={(v) => muter('entrees_coffre',
                        (l) => l.map((x) => x.id === e.id ? { ...x, amount: v } : x))} />
              {auto ? (
                <button className="btn gh sm" title="Le montant se corrige à l’étape 05b"
                        onClick={() => aller({ vue: 'run', date, etape: '06' })}>
                  Corriger en 05b →
                </button>
              ) : (
                <button className="btn stop sm" disabled={fige}
                        aria-label={`Retirer l’entrée ${e.label || e.type}`}
                        onClick={() => muter('entrees_coffre',
                          (l) => l.filter((x) => x.id !== e.id))}>✕</button>
              )}
            </div>
            )
          })}

          {/* Une entrée SCO saisie à la main À CÔTÉ de celle du vidage compte
              l'argent deux fois. On ne l'efface pas — c'est le travail de
              quelqu'un — mais on ne laisse pas passer le doublon en silence. */}
          {inv.entrees_coffre.some((e) => e.id === ID_ENTREE_SCO)
            && inv.entrees_coffre.some((e) => e.type === 'SCO' && e.id !== ID_ENTREE_SCO) && (
            <div style={{ padding: '0 12px 10px' }}>
              <Notice c="stop">
                <b>Deux entrées SCO.</b> Le vidage de l’étape 05b s’inscrit tout seul ; l’entrée
                saisie à la main compte le même argent une seconde fois et gonfle le solde attendu
                d’autant. Retire celle que tu avais saisie.
              </Notice>
            </div>
          )}
          {!fige && (
            <div style={{ padding: '9px 12px' }}>
              <button className="btn sm" onClick={() => setBoite({ sorte: 'entree' })}>Ajouter une entrée</button>
            </div>
          )}
        </Card>
      </div>

      {/* apports — lecture seule, ils viennent de l'étape 02 */}
      <div style={{ marginTop: 12 }}>
        <Card titre="Apports du jour — lecture seule" actions={<span className="mo">{fmtEur(apports.total)}</span>}>
          {apports.lignes.length === 0
            ? <Vide>Aucun apport saisi.</Vide>
            : apports.lignes.map((a) => (
              <div className="slot" key={a.caissier}>
                <span className="nm">{a.caissier}</span>
                {a.ko && <Chip c="stop">KO</Chip>}
                <span className="sp" />
                <span className="mo">{fmtEur(a.total)}</span>
              </div>
            ))}
          <div className="hint" style={{ padding: '8px 12px' }}>
            SOURCE : ÉTAPE 02 · APPORTS — VALORISATION BANCAIRE DE sotcp-core
          </div>
        </Card>
      </div>

      {/* écart et validation */}
      <div style={{ marginTop: 12 }}>
        {/* « VALIDÉ » en vert sur la carte de l'écart, c'est le bandeau vert à
            1 558 € une deuxième fois : validé ne veut pas dire conforme. Le
            jeton prend le ton de l'écart qu'il scelle. */}
        <Card titre="Écart de coffre" lampe={lampeEcart(ecran.ecart)}
              actions={<Chip c={invValide ? lampeEcart(ecran.ecart) : 'neutre'}>
                {invValide ? 'VALIDÉ' : 'EN COURS'}
              </Chip>}>
          <div className="slot">
            <span className="nm">Total compté</span><span className="sp" />
            <span className="mo">{fmtEur(ecran.total_compte)}</span>
          </div>
          <div className="slot">
            <span className="nm">Solde attendu</span><span className="sp" />
            <span className="mo">{fmtEur(ecran.solde_attendu)}</span>
          </div>
          <div className="slot">
            <span className="nm">Écart</span>
            <span className="hint">{motEcart(ecran.ecart).toUpperCase()}</span>
            <span className="sp" />
            <span className="big" style={{ color: couleurEcart(ecran.ecart) }}
                  title={motEcart(ecran.ecart)}>
              {fmtEcart(ecran.ecart)}
            </span>
          </div>

          {(motifObligatoire || motifOrphelin) && (
            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--line)' }}>
              <div className="lbl" style={{ marginBottom: 6 }}>Justification de l’écart</div>
              {!fige && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {/* Causes fréquentes : un sélecteur d'état, pas six actions —
                      la cause retenue reste enfoncée (aria-pressed). */}
                  {CAUSES.map((c) => (
                    <button className="btn sm" key={c} aria-pressed={motif === c}
                            onClick={() => muter('commentaire_ecart', motif === c ? '' : c)}>{c}</button>
                  ))}
                </div>
              )}
              <textarea className="fld" rows={2} disabled={fige} placeholder="Explique l’écart constaté…"
                        aria-label="Justification de l’écart de coffre"
                        style={{ resize: 'vertical' }} value={inv.commentaire_ecart}
                        onChange={(e) => muter('commentaire_ecart', e.target.value)} />
              {motifManque && (
                <div style={{ marginTop: 8 }}>
                  <Notice c="stop">
                    <b>Un motif est obligatoire</b> — le serveur refuse la validation d’un écart
                    de 0,01 € ou plus sans explication. Elle part au bilan et au briefing de demain.
                  </Notice>
                </div>
              )}
              {/* Sur un inventaire figé, ce motif est une pièce du dossier
                  arrêté : plus rien à retirer, et l'alarme « il serait
                  resservi » ne veut plus rien dire. On garde le texte, on
                  enlève l'injonction et son bouton mort. */}
              {motifOrphelin && !fige && (
                <div style={{ marginTop: 8 }}>
                  <Notice c="neutre" action={
                    <button className="btn stop sm"
                            onClick={() => muter('commentaire_ecart', '')}>Retirer le motif</button>
                  }>
                    <b>L’écart est revenu à zéro</b> — ce motif ne justifie plus rien et serait
                    resservi tel quel au prochain écart.
                  </Notice>
                </div>
              )}
            </div>
          )}

          {!invValide && (
            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="hint" style={motifManque ? { color: 'var(--stop)' } : undefined}>
                {motifManque
                  ? 'JUSTIFIE L’ÉCART CI-DESSUS POUR POUVOIR VALIDER'
                  : 'LE SERVEUR RECALCULE TOUT ET ÉCRIT SES PROPRES CHIFFRES'}
              </span>
              <span className="sp" />
              {/* Ton `ok` : ce bouton SOLDE l'inventaire. La garde du motif est
                  ici en plus du refus serveur — un aller-retour pour se faire
                  dire non n'apprend rien à personne. */}
              <button className="btn ok lg"
                      disabled={lectureSeule || journeeFermee || occupe || motifManque}
                      data-encours={occupe ? 'true' : undefined}
                      title={motifManque ? 'Un écart de coffre ne se valide pas sans motif' : undefined}
                      onClick={() => setBoite({ sorte: 'valider' })}>
                {occupe ? 'Validation…' : 'Valider l’inventaire'}
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* quota du mois */}
      <div style={{ marginTop: 12 }}>
        <Card titre="Quota des inventaires du mois">
          <div className="slot">
            <span className="nm">Quota mensuel</span>
            {quota.etat === 'fait' && (
              <Chip c={quota.valides >= quotaInventaires ? 'ok' : 'neutre'}>
                {quota.valides >= quotaInventaires ? 'ATTEINT' : 'EN COURS'}
              </Chip>
            )}
            <span className="sp" />
            <span className="mo">
              {quota.etat === 'fait' ? `${quota.valides} / ${quotaInventaires}` : `— / ${quotaInventaires}`}
            </span>
            <button className="btn sm" disabled={quota.etat === 'compte'}
                    data-encours={quota.etat === 'compte' ? 'true' : undefined}
                    onClick={() => void compter()}>
              {quota.etat === 'compte' ? 'Comptage…' : quota.etat === 'fait' ? 'Recompter' : 'Compter'}
            </button>
          </div>
          <div className="hint" style={{ padding: '0 12px 9px' }}>
            {quota.etat !== 'fait'
              ? 'COMPTÉ À LA DEMANDE — CHAQUE JOURNÉE DU MOIS DOIT ÊTRE RELUE EN ENTIER'
              : quota.journees === 0
                // « 0 sur 0 » n'est pas un quota tenu : c'est un mois sans journée.
                ? 'AUCUNE JOURNÉE OUVERTE DEPUIS LE 1ER DU MOIS — RIEN À COMPTER'
                : `${pluriel(quota.journees, 'JOURNÉE', 'JOURNÉES')} EXAMINÉE${quota.journees > 1 ? 'S' : ''} DEPUIS LE 1ER DU MOIS`}
          </div>
        </Card>
      </div>

      {/* ── modales ─────────────────────────────────────────────────────── */}

      {boite?.sorte === 'fixe' && (
        <ModaleFixe
          fermer={() => setBoite(null)}
          ajouter={(nom, montant) => {
            muter('caisse_fixes', (cf) => [...cf,
              { id: uid(), name: nom, default_amount: montant, confirmed_amount: montant }])
            setBoite(null)
          }}
        />
      )}

      {boite?.sorte === 'enveloppe' && (
        <ModaleEnveloppe
          titre={boite.cible === 'prelevements_j'
            ? 'Composer une enveloppe J (500 €)'
            : 'Composer une enveloppe J+1 (500 €)'}
          fermer={() => setBoite(null)}
          ajouter={(env) => {
            muter(boite.cible, (l) => [...l, env])
            setBoite(null)
          }}
        />
      )}

      {boite?.sorte === 'entree' && (
        <Modale titre="Type d’entrée" fermer={() => setBoite(null)}
                sous="Le type sert au rapprochement : une réintégration FDC ne se lit pas comme une livraison de monnaie, et l’argent trouvé se dicte à part à l’étape 11.">
          {TYPES_ENTREE.map((t) => (
            <button className="btn" key={t.type} style={{ width: '100%', marginBottom: 6, textAlign: 'left' }}
                    onClick={() => {
                      const id = uid()
                      muter('entrees_coffre',
                        (l) => [...l, { id, type: t.type, label: t.label, amount: 0 }])
                      // Le montant est la seule chose qui reste à taper : on y va.
                      setEntreeNeuve(id)
                      setBoite(null)
                    }}>
              <Chip c={COULEUR_ENTREE[t.type] ?? 'neutre'}>{t.type}</Chip>{' '}
              {t.nom}
            </button>
          ))}
        </Modale>
      )}

      {boite?.sorte === 'valider' && (
        <Modale titre="Valider l’inventaire ?" fermer={() => setBoite(null)}
                sous="Les sections passeront en lecture seule. Le serveur recalcule les totaux et écrit les siens ; la réouverture reste possible.">
          <div className="slot" style={{ borderBottom: 'none' }}>
            <span className="nm">Écart soumis</span>
            <span className="sp" />
            <span className="mo" style={{ color: couleurEcart(ecran.ecart) }}>
              {fmtEcart(ecran.ecart)} · {motEcart(ecran.ecart)}
            </span>
          </div>
          {/* Le bouton de confirmation prend le focus : Entrée confirme, Échap
              ferme (Modale). Une confirmation qui oblige à viser à la souris
              coûte deux secondes à chaque fois, debout, gants aux mains. */}
          <div className="btns">
            <button className="btn" onClick={() => setBoite(null)}>Annuler</button>
            <button className="btn ok" autoFocus data-encours={occupe ? 'true' : undefined}
                    onClick={() => void valider()}>Valider l’inventaire</button>
          </div>
        </Modale>
      )}

      {boite?.sorte === 'rouvrir' && (
        <Modale titre="Rouvrir l’inventaire ?" fermer={() => setBoite(null)}
                sous="Les sections redeviennent saisissables. La réouverture est inscrite au journal.">
          <div className="btns">
            <button className="btn" onClick={() => setBoite(null)}>Annuler</button>
            <button className="btn warn" autoFocus data-encours={occupe ? 'true' : undefined}
                    onClick={() => void rouvrir()}>Rouvrir l’inventaire</button>
          </div>
        </Modale>
      )}
    </>
  )
}

/* ── pièces d'écran ──────────────────────────────────────────────────────── */

/**
 * Champ numérique du coffre. Trois raisons de ne pas être un `input[type=number]`
 * contrôlé sur un nombre :
 *
 *   • le pavé numérique français émet une VIRGULE, qu'un input[type=number]
 *     tient pour invalide : `e.target.value` vaut alors '' et le champ se vide
 *     sous les doigts ;
 *   • « 12. » n'est pas non plus un nombre valide — impossible de taper un
 *     montant à décimales sans que la frappe soit avalée au séparateur ;
 *   • repasser par String(valeur) à chaque frappe efface le 0 de tête (« 0,05 »).
 *
 * D'où un tampon de texte tant que le champ a le focus, relâché au blur : la
 * valeur affichée redevient alors celle de l'état, y compris si le Honeywell
 * l'a changée entre-temps. Le contenu se sélectionne au focus — on recompte,
 * on ne corrige pas.
 */
function Saisie({ valeur, changer, fige, entier = false, largeur = 92, etiquette, id,
                 autoFocus = false, surTouche }: {
  valeur: number
  changer: (v: number) => void
  fige: boolean
  /** Quantité de rouleaux / pièces / billets : entier positif obligatoire. */
  entier?: boolean
  largeur?: number | string
  etiquette?: string
  id?: string
  autoFocus?: boolean
  surTouche?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  const [brut, setBrut] = useState<string | null>(null)
  // Au repos, on rend le nombre comme le reste de l'écran le lit : à la
  // française. `String(0.05)` afficherait « 0.05 » dans un champ d'euros au
  // milieu de montants en « 0,05 € ». La saisie, elle, accepte les deux.
  const affiche = brut ?? (valeur === 0 ? '' : String(valeur).replace('.', ','))

  const saisir = (texte: string) => {
    let filtre: string
    if (entier) {
      // On COUPE au séparateur, on ne le supprime pas : « 2,5 » doit donner 2
      // (le plancher, ce que fait aussi entierPositif), jamais 25. Effacer la
      // virgule collerait les chiffres et multiplierait le comptage par dix —
      // 25 rouleaux de 1 € au lieu de 2, soit 575 € d'écart fantôme, sans que
      // rien à l'écran ne signale la faute de frappe.
      filtre = (texte.split(/[.,]/)[0] ?? '').replace(/[^0-9]/g, '')
    } else {
      // Le tampon garde le séparateur TEL QUE TAPÉ : normaliser la virgule en
      // point à la frappe ferait sauter « 12,5 » en « 12.5 » sous les doigts,
      // et le curseur avec. Un seul séparateur, le premier : « 12,3,4 » se
      // lit « 12,34 ». La normalisation n'a lieu que pour la valeur remontée.
      const net = texte.replace(/[^0-9.,]/g, '')
      const sep = net.search(/[.,]/)
      filtre = sep < 0 ? net : net.slice(0, sep + 1) + net.slice(sep + 1).replace(/[.,]/g, '')
    }
    setBrut(filtre)
    // « », « , » et « . » ne sont pas des nombres : ce sont des saisies en
    // cours. Elles valent zéro sans effacer ce qui est affiché.
    const point = filtre.replace(',', '.')
    changer(point === '' || point === '.' ? 0 : safeNumber(point, 0))
  }

  return (
    <input className="fld mo" type="text" inputMode="decimal" autoComplete="off"
           disabled={fige} placeholder="0" aria-label={etiquette} id={id}
           autoFocus={autoFocus}
           style={{ width: largeur, textAlign: 'right', padding: '5px 7px', marginBottom: 0 }}
           value={affiche}
           onFocus={(e) => e.currentTarget.select()}
           onBlur={() => setBrut(null)}
           onKeyDown={surTouche}
           onChange={(e) => saisir(e.target.value)} />
  )
}

function TableDenoms({ lignes, fige, changer, colonneVenue }: {
  lignes: Array<{
    cle: string; libelle: string; sous?: string; quantite: number; unite: number
    /** Ce qui s'ajoute sans avoir été compté — les enveloppes scellées de 05b. */
    venu?: number
  }>
  fige: boolean
  changer: (cle: string, q: number) => void
  /** En-tête de la colonne « venu d'ailleurs », affichée seulement si utile. */
  colonneVenue?: string
}) {
  // La colonne ne s'ouvre que s'il y a quelque chose dedans : une colonne de
  // zéros sur onze lignes, tous les jours, ne dit rien et prend la place.
  const avecVenu = !!colonneVenue && lignes.some((l) => (l.venu ?? 0) > 0)
  return (
    <table className="t">
      {avecVenu && (
        <thead>
          <tr>
            <th />
            <th className="n">Comptés</th>
            <th className="n">{colonneVenue}</th>
            <th className="n">Total</th>
            <th className="n">Valeur</th>
          </tr>
        </thead>
      )}
      <tbody>
        {lignes.map((l) => {
          const venu = l.venu ?? 0
          const total = l.quantite + venu
          return (
            <tr key={l.cle}>
              <td>
                <span className="mo">{l.libelle}</span>
                {l.sous && <span className="hint"> · {l.sous}</span>}
              </td>
              <td style={{ width: 96 }}>
                <Saisie valeur={l.quantite} fige={fige} largeur={80} entier
                        etiquette={`Quantité — ${l.libelle}`} surTouche={avancerDansTable}
                        changer={(q) => changer(l.cle, q)} />
              </td>
              {avecVenu && (
                <>
                  <td className="n mo" style={{ color: venu > 0 ? 'var(--act)' : 'var(--ink4)' }}>
                    {venu > 0 ? `+ ${venu}` : '—'}
                  </td>
                  <td className="n mo" style={{ fontWeight: total > 0 ? 600 : 400 }}>{total}</td>
                </>
              )}
              <td className="n" style={{ color: total > 0 ? 'var(--ink)' : 'var(--ink4)' }}>
                {fmtEur(total * l.unite)}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function ListeEnveloppes({ enveloppes, fige, vide, ajouter, retirer, composer, numeroter }: {
  enveloppes: Enveloppe[]
  fige: boolean
  vide: string
  ajouter: (env: Enveloppe) => void
  retirer: (id: string) => void
  composer: () => void
  /** Seules les enveloppes du jour portent un numéro (réponse de Kamel, §8.3). */
  numeroter?: (id: string, numero: string) => void
}) {
  return (
    <>
      {!fige && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '9px 12px', borderBottom: '1px solid var(--line)' }}>
          {/* Le détail est RECOPIÉ : `p.detail` est une constante du paquet
              partagé. La poser telle quelle dans l'état ferait tenir deux
              enveloppes par le même objet — et le preset lui-même. */}
          {J1_PRESETS.map((p) => (
            <button className="btn sm" key={p.label}
                    onClick={() => ajouter({
                      id: uid(), amount: VALEUR_ENVELOPPE, detail: p.detail.map((d) => ({ ...d })),
                    })}>{p.label}</button>
          ))}
          <button className="btn gh sm" onClick={composer}>Composition manuelle</button>
        </div>
      )}
      {enveloppes.length === 0
        ? <Vide>{vide}</Vide>
        : enveloppes.map((env, i) => (
          <div className="slot" key={env.id}>
            <span className="nm">Enveloppe {i + 1}</span>
            {/* Le numéro de scellé — c'est lui que COL MNC demande sur l'écran
                « Prélèvement d'espèces du coffre monnaie ». Kamel : seul le
                prélèvement J en porte un, pas celui préparé pour demain. */}
            {numeroter && (
              <input className="fld mo" style={{ width: 116, padding: '4px 7px' }}
                     placeholder="N° scellé" aria-label={`Numéro de l’enveloppe ${i + 1}`}
                     disabled={fige} value={env.numero ?? ''}
                     onChange={(e) => numeroter(env.id, e.target.value)} />
            )}
            <span className="hint mo">
              {env.detail.map((d) => `${d.quantite}×${d.denomination}€`).join(' + ') || '—'}
            </span>
            <span className="sp" />
            <span className="mo">{fmtEur(env.amount)}</span>
            {/* Une enveloppe se rajoute en un clic depuis les presets : la
                retirer est réversible, on ne demande pas confirmation — mais
                le ton dit que ça détruit. */}
            <button className="btn stop sm" disabled={fige} aria-label={`Retirer l’enveloppe ${i + 1}`}
                    onClick={() => retirer(env.id)}>✕</button>
          </div>
        ))}
    </>
  )
}

function ModaleFixe({ ajouter, fermer }: { ajouter: (nom: string, montant: number) => void; fermer: () => void }) {
  const [nom, setNom] = useState('')
  const [montant, setMontant] = useState(0)
  const possible = nom.trim().length > 0 && montant > 0
  return (
    <Modale titre="Ajouter une caisse fixe" fermer={fermer}
            sous="Une caisse fixe ajoutée ici ne vaut que pour cette journée — les caisses permanentes se règlent dans SotCP.">
      {/* Entrée valide, Échap ferme (Modale) : la modale se traverse sans souris. */}
      <form onSubmit={(e) => { e.preventDefault(); if (possible) ajouter(nom.trim(), montant) }}>
        <label className="lbl" htmlFor="cf-nom">Nom</label>
        <input id="cf-nom" autoFocus value={nom} placeholder="ex. 320" autoComplete="off"
               onChange={(e) => setNom(e.target.value)} />
        <label className="lbl" htmlFor="cf-mnt">Montant par défaut (€)</label>
        <div style={{ marginBottom: 10 }}>
          <Saisie valeur={montant} changer={setMontant} fige={false} largeur="100%" id="cf-mnt"
                  etiquette="Montant par défaut de la caisse fixe" />
        </div>
        <div className="btns">
          <button type="button" className="btn" onClick={fermer}>Annuler</button>
          <button type="submit" className="btn ok" disabled={!possible}>Ajouter la caisse fixe</button>
        </div>
      </form>
    </Modale>
  )
}

/** Une enveloppe vaut 500 €, jamais autre chose : c'est la garde de la V2 et
 *  c'est ce qui rend les enveloppes comptables à l'unité. */
function ModaleEnveloppe({ titre, ajouter, fermer }: {
  titre: string; ajouter: (env: Enveloppe) => void; fermer: () => void
}) {
  const [qte, setQte] = useState<Record<string, number>>(
    () => Object.fromEntries(BILLET_DENOMS.map((d) => [BILLET_KEY(d), 0])))
  const total = BILLET_DENOMS.reduce((s, d) => s + (qte[BILLET_KEY(d)] ?? 0) * d, 0)
  // L'écart à 500 € est un écart : même échelle, même couleur, même signe.
  const reste = total - VALEUR_ENVELOPPE
  const possible = reste === 0

  return (
    <Modale titre={titre} fermer={fermer} large
            sous="Le détail des coupures part avec l'enveloppe : c'est lui qu'on relit le jour où une enveloppe manque.">
      {/* Entrée valide dès que le compte est bon — le bouton désactivé bloque
          la soumission implicite tant qu'on n'est pas à 500 €. */}
      <form onSubmit={(e) => {
        e.preventDefault()
        if (!possible) return
        ajouter({
          id: uid(), amount: VALEUR_ENVELOPPE,
          detail: BILLET_DENOMS.filter((d) => (qte[BILLET_KEY(d)] ?? 0) > 0).map((d) => ({
            denomination: d, quantite: qte[BILLET_KEY(d)] ?? 0, total: (qte[BILLET_KEY(d)] ?? 0) * d,
          })),
        })
      }}>
        <table className="t" style={{ marginBottom: 10 }}>
          <tbody>
            {BILLET_DENOMS.map((d, i) => {
              const cle = BILLET_KEY(d)
              return (
                <tr key={cle}>
                  <td className="mo">{d} €</td>
                  <td style={{ width: 96 }}>
                    <Saisie valeur={qte[cle] ?? 0} fige={false} largeur={80} entier
                            autoFocus={i === 0} etiquette={`Nombre de billets de ${d} €`}
                            surTouche={avancerDansTable}
                            changer={(q) => setQte((p) => ({ ...p, [cle]: q }))} />
                  </td>
                  <td className="n">{fmtEur((qte[cle] ?? 0) * d)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className="slot" style={{ borderBottom: 'none' }}>
          <span className="nm">Total</span>
          <span className="sp" />
          {!possible && (
            <span className="hint mo" style={{ color: couleurEcart(reste) }}>
              {reste < 0 ? `IL MANQUE ${fmtEur(-reste)}` : `${fmtEur(reste)} DE TROP`}
            </span>
          )}
          <span className="big" style={{ color: total === 0 ? 'var(--ink4)' : couleurEcart(reste) }}>
            {fmtEur(total)}
          </span>
        </div>
        <div className="btns">
          <button type="button" className="btn" onClick={fermer}>Annuler</button>
          <button type="submit" className="btn ok" disabled={!possible}>Ajouter l’enveloppe</button>
        </div>
      </form>
    </Modale>
  )
}
